<template>
  <div>
    <BasePaymentRequestsTable
      :query="query"
      data-test="payment-requests-to-be-payrolled-table"
      :is-active-tab="isActiveTab"
      :bulk-actions="bulkActions"
    >
      <template #action="{ row }">
        <PaymentRequestActionColumnToBePayrolled
          :row="row"
          @payment-request-updated="$emit('payment-request-updated')"
        />
      </template>
    </BasePaymentRequestsTable>
  </div>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { useMutation } from '@vue/apollo-composable'
import BasePaymentRequestsTable from './BasePaymentRequestsTable.vue'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { useToast } from '@/composables/useToast'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { CreateBatchMutation } from '@/components/GraphQL/mutations/Batch/CreateBatchMutation'
import { RunBatchActionMutation } from '@/components/GraphQL/mutations/Batch/RunBatchActionMutation'
import { BatchActionType, BatchType } from '@/gql/graphql'
import PaymentRequestActionColumnToBePayrolled from '@/components/GraphqlDataTable/columns/PaymentRequest/Partner/PaymentRequestActionColumnToBePayrolled.vue'

const query = graphql(`
  query PartnerPaymentRequestsToBePayrolledQuery(
    $accounts: [ID!]
    $search: String
    $first: Int!
    $page: Int
    $orderBy: [PaymentRequestOrderByClauseInput!]
    $timesheetPeriod: DateRangeInput
    $hasExpenses: Boolean
    $jobs: [ID!]
    $clients: [ID!]
  ) {
    paymentRequests(
      accounts: $accounts
      search: $search
      first: $first
      page: $page
      orderBy: $orderBy
      timesheetPeriod: $timesheetPeriod
      hasExpenses: $hasExpenses
      jobs: $jobs
      clients: $clients
      statuses: [APPROVED]
      hasBatch: false
    ) {
      data {
        ...PaymentRequestClientApprovalDateColumn
        ...PaymentRequestPrIdColumn
        ...PaymentRequestWorkerColumn
        ...PaymentRequestClientColumn
        ...PaymentRequestTimesheetPeriodColumn
        ...PaymentRequestRateColumn
        ...PaymentRequestUnitsColumn
        ...PaymentRequestExpensesColumn
        ...PaymentRequestJobColumn
        ...PaymentRequestClientPaymentStatusColumn
        ...PaymentRequestAssignmentCompanyColumn
        ...PaymentRequestWorkLocationColumn
        ...PaymentRequestActionColumnToBePayrolled
      }
    }
  }
`) as TypedQueryDocumentNode

defineProps<{ isActiveTab?: boolean }>()

const toast = useToast()

const emit = defineEmits<{
  'payment-request-updated': []
}>()

const { mutate: createBatch } = useMutation(CreateBatchMutation, {
  clientId: 'publicApi',
})
const { mutate: runBatchAction } = useMutation(RunBatchActionMutation, {
  clientId: 'publicApi',
})

const bulkActions: DataTableBulkAction[] = [
  {
    name: $t('Export batch and move to processing'),
    icon: 'download' as any,
    type: 'primary',
    dataTest: 'export-batch-and-move-to-processing-bulk',
    action: async (selectedRows) => {
      const paymentRequestIds = Object.keys(selectedRows)

      try {
        const result = await createBatch({
          input: {
            account: currentAccount.globalId,
            type: BatchType.PartnerPaymentRequests,
            items: paymentRequestIds,
          },
        })

        const batchName = result?.data?.createBatch?.name
        const newBatchId = result?.data?.createBatch?.id
        const count = result?.data?.createBatch?.items?.data?.length || paymentRequestIds.length

        toast.success(
          $t('Successfully created batch :batchName with :count payment requests', {
            batchName,
            count,
          }),
        )

        if (newBatchId) {
          const exportRes = await runBatchAction({
            input: {
              batch: newBatchId,
              action: BatchActionType.Export,
              deleteIfEmptied: false,
            },
          })
          const url = exportRes?.data?.runBatchAction?.resultUrl
          if (url) {
            const a = document.createElement('a')
            a.href = url
            a.rel = 'noopener'
            a.download = ''
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        }

        emit('payment-request-updated')
      } catch (error) {
        throw new Error($t('Failed to create payment request batch'))
      }
    },
  },
]
</script>

<style scoped></style>
